import { Loader } from 'components/Loader';
import React, { useEffect, useMemo, useState } from 'react';
import { GetFlagsResponse, PhotoSize } from 'redux/reducers/moderation/types';
import { formatDateTime } from 'untils/formatDateTime';
import { calcTotalPhotoSize } from 'untils/formatFileSize';
import { fetchWithAuthentication } from 'untils/getFile';
import { getProtectedImageBlob } from 'untils/imageCache';
import { decodeRestriction } from 'untils/transformFlagCounts';

export interface ProblemMediaCardProps {
  previewUrl?: string | null; // URL для превʼю
  filename: string; // назва файлу
  photoSizes: PhotoSize[]; // масив розмірів фото (було: sizeMB)
  resolution?: string; // опційно, якщо хочеш форсити
  mimeLabel: string; // "Image (JPG)" / "Video (MP4)" ...
  uploadedAt: string; // ISO або інший формат, який підтримає formatDateTime
  complaint: number; // код скарги
  hashtags?: string[];
  flags: GetFlagsResponse | null;
  hash: string; // тригер оновлення (версія/etag)
}

export const ProblemMediaCard: React.FC<ProblemMediaCardProps> = ({
  previewUrl,
  filename,
  photoSizes,
  resolution: resolutionProp,
  mimeLabel,
  uploadedAt,
  complaint,
  hashtags = [],
  flags,
  hash,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const best = useMemo(() => {
    if (!photoSizes?.length) return null;
    return [...photoSizes].sort((a, b) => b.w * b.h - a.w * a.h)[0];
  }, [photoSizes]);

  const resolution = useMemo(() => {
    if (resolutionProp) return resolutionProp;
    if (!best) return '';
    return `${best.w}×${best.h}`;
  }, [resolutionProp, best]);

  const totalHuman = useMemo(() => calcTotalPhotoSize(photoSizes), [photoSizes]);
  const metaLine = `${totalHuman}${resolution ? `, ${resolution}` : ''}`;

  // Завантаження захищеного зображення
  const [src, setSrc] = useState<string>();
  const [bigImage, setBigImage] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') || '' : '';

  useEffect(() => {
    if (!previewUrl) {
      setSrc(undefined);
      return;
    }

    let alive = true;
    let currentObjectUrl: string | undefined;

    getProtectedImageBlob(previewUrl, token, fetchWithAuthentication, setLoading)
      .then((url) => {
        if (!alive) return;
        setLoading(false);
        currentObjectUrl = URL.createObjectURL(url);
        setSrc(currentObjectUrl);
      })
      .catch(() => {
        if (alive) setSrc(undefined);
        setLoading(false);
      });

    return () => {
      alive = false;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
    // hash у залежностях — щоб примусово перезавантажувати превʼю при зміні версії
  }, [previewUrl, token, hash]);

  return (
    <section className="pm-card" role="group" aria-label="Problematic media">
      <header className="pm-card__header">
        <span className="pm-card__emoji" aria-hidden>
          🖼️
        </span>
        <h3 className="pm-card__title">Problematic Content</h3>
      </header>

      <div className="pm-card__grid">
        {/* LEFT: description */}
        <ul className="pm-card__info">
          <li className="pm-card__info-row">
            <span className="pm-card__info-label">Type:</span>
            <span className="pm-card__info-value">{mimeLabel}</span>
          </li>

          <li className="pm-card__info-row">
            <span className="pm-card__info-label">Size:</span>
            <span className="pm-card__info-value">{totalHuman}</span>
          </li>

          <li className="pm-card__info-row">
            <span className="pm-card__info-label">Uploaded:</span>
            <span className="pm-card__info-value">{formatDateTime(uploadedAt)}</span>
          </li>

          <li className="pm-card__info-row">
            <span className="pm-card__info-label">Complaint:</span>
            <span className="pm-card__info-value">
              {decodeRestriction(complaint, flags?.data || [])
                .map((f) => f.description)
                .join(', ') || '—'}
            </span>
          </li>

          {/* Якщо хочеш показувати «best» розмір окремо: */}
          {/* <li className="pm-card__info-row">
            <span className="pm-card__info-label">Best:</span>
            <span className="pm-card__info-value">
              {best ? `${bestSizeHuman}, ${best.w}×${best.h}` : '—'}
            </span>
          </li> */}
        </ul>

        {/* RIGHT: file preview */}
        {loading ? (
          <div className="wrapper-loader">
            <Loader />
          </div>
        ) : (
          <div className="pm-card__preview">
            {bigImage && (
              <div className="preview-absolute" onClick={() => setBigImage(false)}>
                <div className="preview-absolute__inner" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="preview-absolute__close"
                    onClick={() => setBigImage(false)}
                    aria-label="Close preview"
                  >
                    ×
                  </button>
                  {src ? (
                    <img className="preview-absolute__img" src={src} alt={filename} />
                  ) : (
                    <div className="pm-card__placeholder" aria-label="Media for moderation">
                      <span className="pm-card__placeholder-title">[MEDIA FOR MODERATION]</span>
                      <div className="pm-card__file">
                        <span className="pm-card__file-emoji" aria-hidden>
                          🖼️
                        </span>
                        <span className="pm-card__file-name">{filename}</span>
                        <span className="pm-card__file-meta">{metaLine}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {src ? (
              mimeLabel?.includes('video') ? (
                <video className="pm-card__img" src={src} controls playsInline preload="metadata" />
              ) : (
                <img
                  onClick={() => setBigImage(true)}
                  className="pm-card__img"
                  src={src}
                  alt={filename}
                  loading="lazy"
                />
              )
            ) : (
              <div className="pm-card__placeholder" aria-label="Media for moderation">
                <span className="pm-card__placeholder-title">[MEDIA FOR MODERATION]</span>
                <div className="pm-card__file">
                  <span className="pm-card__file-emoji" aria-hidden>
                    🖼️
                  </span>
                  <span className="pm-card__file-name">{filename}</span>
                  <span className="pm-card__file-meta">{metaLine}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
